/**
 * Бренды платформы.
 *
 * Здесь три решения: подтвердить бренд, снять его с витрины и поправить
 * сферу, если автоматика разложила категорию не туда. Блокировка не удаляет
 * ни бренд, ни его акции — у участников на руках билеты и незабранные призы,
 * и стереть бренд значило бы стереть их доказательства.
 */
import type { Brand, BrandInvite } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import {
  Badge, BrandMark, Button, Card, Empty, REGIONS, Segmented, Skeleton, fmtShortDay, num,
} from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

export function BrandsScreen() {
  const data = useResource(() => api.adminBrands(), []);
  const { me, canModerate } = useAdmin();
  const toast = useToast();
  const [blocking, setBlocking] = useState<Brand | null>(null);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState<Brand | null>(null);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [q, setQ] = useState('');
  // Витрина и настоящие партнёры живут в одном списке: это один и тот же
  // объект, и смешивать их в глазах платформы нельзя, а разносить по разным
  // экранам — значит потерять из виду, что витрина тоже видна покупателю.
  const [kind, setKind] = useState<'all' | 'demo' | 'live'>('all');

  // Поиск на клиенте: брендов на платформе не тысячи, а запрос к серверу на
  // каждую букву — это задержка там, где её быть не должно. Когда список
  // перестанет помещаться в один ответ, искать будет сервер.
  const found = (data.data?.brands ?? [])
    .filter((b) => (kind === 'all' ? true : kind === 'demo' ? b.demo : !b.demo))
    .filter((b) =>
      `${b.name} ${b.category} ${b.slug}`.toLowerCase().includes(q.trim().toLowerCase()),
    );
  const demoCount = (data.data?.brands ?? []).filter((b) => b.demo).length;

  const patch = async (
    brand: Brand,
    body: {
      verified?: boolean;
      blocked?: boolean;
      sphere?: string;
      reason?: string;
      video_upload?: '' | 'on' | 'off';
      demo?: boolean;
    },
    done: string,
  ) => {
    try {
      await api.adminSetBrand(brand.id, body);
      toast(done);
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <Layout title="Бренды" subtitle="заведение, приглашение, проверка и разрез ленты">
      <ReadOnlyNote />

      {canModerate && (
        <div className="row" style={{ marginBottom: 14 }}>
          <Button variant="pri" onClick={() => setCreating(true)}>
            Завести бренд
          </Button>
          <span className="spacer" />
        </div>
      )}

      {data.data && data.data.brands.length > 0 && (
        <div className="row" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="Поиск по названию, категории или адресу"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 420 }}
          />
          <Segmented
            value={kind}
            options={[
              { value: 'all', label: `Все ${data.data.brands.length}` },
              { value: 'demo', label: `Витрина ${demoCount}` },
              { value: 'live', label: `Настоящие ${data.data.brands.length - demoCount}` },
            ]}
            onChange={setKind}
          />
        </div>
      )}

      {data.loading && <Skeleton count={4} height={56} />}
      {data.data && data.data.brands.length === 0 && <Empty icon="building" title="Брендов нет" />}
      {data.data && data.data.brands.length > 0 && found.length === 0 && (
        <Empty icon="search" title="Ничего не нашлось" />
      )}

      {found.length > 0 && (
        <Card>
          <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>Бренд</th>
                <th>Сфера</th>
                <th>Страны</th>
                <th>Рейтинг</th>
                <th>Витрина</th>
                <th>Ролики</th>
                <th>Состояние</th>
                {canModerate && <th />}
              </tr>
            </thead>
            <tbody>
              {found.map((brand) => (
                <tr key={brand.id}>
                  <td>
                    <div className="row" style={{ gap: 8 }}>
                      <BrandMark name={brand.name} logoUrl={brand.logo_url} size={22} />
                      <div style={{ minWidth: 0 }}>
                        <div>{brand.name}</div>
                        <div className="faint" style={{ fontSize: 11.5 }}>
                          {brand.category || '— без категории'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {canModerate ? (
                      <select
                        className="select"
                        value={brand.sphere_override ?? ''}
                        onChange={(e) =>
                          void patch(brand, { sphere: e.target.value }, 'Сфера обновлена')
                        }
                      >
                        <option value="">
                          авто — {brand.sphere_label ?? 'Другое'}
                        </option>
                        {(me?.spheres ?? []).map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      brand.sphere_label
                    )}
                  </td>
                  <td className="mono">{brand.regions.join(' · ')}</td>
                  <td>
                    {brand.rating && brand.rating.count > 0
                      ? `${(brand.rating.value / 100).toFixed(2)} · ${num(brand.rating.count)}`
                      : '—'}
                  </td>
                  <td>
                    {/* Витрина нужна, чтобы лента не пустовала, пока нет
                        настоящих партнёров. Пометка уходит и на акции бренда:
                        компания-витрина не может проводить настоящий
                        розыгрыш — приза за ним нет и выдавать его некому. */}
                    {canModerate ? (
                      <button
                        type="button"
                        className={`chip ${brand.demo ? 'on' : ''}`}
                        onClick={() => {
                          const next = !brand.demo;
                          if (
                            next &&
                            !window.confirm(
                              `Пометить «${brand.name}» витриной?\n\nВсе её акции станут ` +
                                'демонстрационными: покупатель увидит их как обычные, но ' +
                                'участие сервер проводить не будет.',
                            )
                          ) {
                            return;
                          }
                          void patch(
                            brand,
                            { demo: next },
                            next ? 'Компания помечена витриной' : 'Компания стала настоящей',
                          );
                        }}
                      >
                        {brand.demo ? 'витрина' : 'настоящая'}
                      </button>
                    ) : (
                      <span className="faint">{brand.demo ? 'витрина' : 'настоящая'}</span>
                    )}
                  </td>
                  <td>
                    {/* Исключение из платформенной настройки загрузки роликов.
                        «Как у платформы» — отдельное состояние, а не выключено:
                        иначе разрешение одному бренду слетало бы при каждой
                        смене общей настройки. */}
                    {canModerate ? (
                      <select
                        className="select"
                        value={brand.video_upload ?? ''}
                        onChange={(e) =>
                          void patch(
                            brand,
                            { video_upload: e.target.value as '' | 'on' | 'off' },
                            'Загрузка роликов обновлена',
                          )
                        }
                      >
                        <option value="">как у платформы</option>
                        <option value="on">разрешить</option>
                        <option value="off">запретить</option>
                      </select>
                    ) : (
                      { '': 'как у платформы', on: 'разрешено', off: 'запрещено' }[
                        brand.video_upload ?? ''
                      ]
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      {brand.verified_at && <Badge tone="good">проверен</Badge>}
                      {brand.blocked_at && <Badge tone="bad">заблокирован</Badge>}
                      {!brand.verified_at && !brand.blocked_at && (
                        <span className="faint" style={{ fontSize: 12 }}>
                          не проверен
                        </span>
                      )}
                    </div>
                    {brand.blocked_reason && (
                      <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>
                        {brand.blocked_reason}
                      </div>
                    )}
                  </td>
                  {canModerate && (
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <Button
                          size="sm"
                          onClick={() =>
                            void patch(
                              brand,
                              { verified: !brand.verified_at },
                              brand.verified_at ? 'Отметка снята' : 'Бренд отмечен проверенным',
                            )
                          }
                        >
                          {brand.verified_at ? 'Снять отметку' : 'Проверен'}
                        </Button>
                        <Button size="sm" onClick={() => setEditing(brand)}>
                          Править
                        </Button>
                        <Button size="sm" onClick={() => setInviting(brand)}>
                          Пригласить
                        </Button>
                        {brand.blocked_at ? (
                          <Button
                            size="sm"
                            onClick={() => void patch(brand, { blocked: false }, 'Бренд разблокирован')}
                          >
                            Разблокировать
                          </Button>
                        ) : (
                          <Button size="sm" variant="danger" onClick={() => setBlocking(brand)}>
                            Заблокировать
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {creating && (
        <CreateBrandDialog
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            data.reload();
          }}
        />
      )}

      {editing && (
        <EditBrandDialog
          brand={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}

      {inviting && (
        <InviteDialog
          brand={inviting}
          onClose={() => setInviting(null)}
        />
      )}

      {blocking && (
        <BlockDialog
          brand={blocking}
          onClose={() => setBlocking(null)}
          onDone={() => {
            setBlocking(null);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

function BlockDialog({
  brand,
  onClose,
  onDone,
}: {
  brand: Brand;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminSetBrand(brand.id, { blocked: true, reason });
      toast('Бренд снят с витрины');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="h2">Заблокировать «{brand.name}»</div>
        <p className="sub" style={{ margin: '6px 0 12px' }}>
          Бренд и его акции останутся в базе: у участников на руках билеты и незабранные призы, и
          стереть бренд значило бы стереть их доказательства. Блокировка убирает его с витрины.
        </p>
        <label className="lab" htmlFor="why">
          Причина
        </label>
        <textarea
          id="why"
          className="input"
          rows={3}
          placeholder="Например: призы не выдаются более 30 дней после срока"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button variant="danger" disabled={busy || reason.trim().length < 4} onClick={submit}>
            Заблокировать
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Правка данных бренда.
 *
 * Отдельный диалог, а не повторное заведение: правила другие. Название
 * меняется всегда — опечатка в нём видна на карточке и в правилах, и чинить
 * её надо. Адрес в ссылке — только пока у бренда нет ни одной запущенной
 * акции: после запуска ссылка /бренд/акция напечатана на упаковке, и смена
 * адреса ломает бумажку в руках у человека. Решает это сервер, здесь лишь
 * показывается отказ.
 *
 * Пометок платформы (проверен, заблокирован, витрина, сфера) здесь нет — у
 * них свои кнопки в строке и свои записи в журнале.
 */
function EditBrandDialog({
  brand,
  onClose,
  onDone,
}: {
  brand: Brand;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: brand.name,
    slug: brand.slug,
    category: brand.category ?? '',
    organizer: brand.organizer ?? '',
    legal_id: brand.legal_id ?? '',
    contact: brand.contact ?? '',
    about: brand.about ?? '',
  });
  const [regions, setRegions] = useState<string[]>(brand.regions ?? []);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateBrand(brand.id, { ...form, regions });
      toast('Бренд обновлён');
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <Card className="box raised" style={{ maxWidth: 520 }}>
        <div className="h2" style={{ marginBottom: 10 }}>«{brand.name}»</div>

        <label className="lab" htmlFor="ebname">Название</label>
        <input
          id="ebname"
          className="input"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />

        <label className="lab" htmlFor="ebslug" style={{ marginTop: 10 }}>Адрес в ссылке</label>
        <input
          id="ebslug"
          className="input mono"
          value={form.slug}
          onChange={(e) => set({ slug: e.target.value })}
        />
        <p className="sub" style={{ fontSize: 11.5, marginTop: 4 }}>
          /{form.slug || 'бренд'}/акция. После запуска первой акции адрес не меняется — ссылка уже
          на упаковке.
        </p>

        <label className="lab" style={{ marginTop: 10 }}>Категория</label>
        <CategoryPicker value={form.category} onPick={(category) => set({ category })} />

        <label className="lab" style={{ marginTop: 10 }}>Страны</label>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          {REGIONS.map((r) => (
            <button
              key={r.code}
              type="button"
              className={`chip ${regions.includes(r.code) ? 'on' : ''}`}
              onClick={() =>
                setRegions((prev) =>
                  prev.includes(r.code) ? prev.filter((x) => x !== r.code) : [...prev, r.code],
                )
              }
            >
              {r.name}
            </button>
          ))}
        </div>

        <label className="lab" htmlFor="eborg" style={{ marginTop: 10 }}>Организатор</label>
        <input
          id="eborg"
          className="input"
          placeholder="ООО «Компания»"
          value={form.organizer}
          onChange={(e) => set({ organizer: e.target.value })}
        />
        <p className="sub" style={{ fontSize: 11.5, marginTop: 4 }}>
          Юридическое имя: оно печатается в правилах каждой акции бренда.
        </p>

        <div className="grid c2" style={{ marginTop: 10 }}>
          <div>
            <label className="lab" htmlFor="eblegal">Реквизиты</label>
            <input
              id="eblegal"
              className="input"
              value={form.legal_id}
              onChange={(e) => set({ legal_id: e.target.value })}
            />
          </div>
          <div>
            <label className="lab" htmlFor="ebcontact">Контакт для участников</label>
            <input
              id="ebcontact"
              className="input"
              value={form.contact}
              onChange={(e) => set({ contact: e.target.value })}
            />
          </div>
        </div>

        <label className="lab" htmlFor="ebabout" style={{ marginTop: 10 }}>О компании</label>
        <textarea
          id="ebabout"
          className="input"
          rows={3}
          value={form.about}
          onChange={(e) => set({ about: e.target.value })}
        />

        {error && (
          <p className="sub" style={{ color: 'var(--bad)', marginTop: 10 }} role="alert">
            {error}
          </p>
        )}

        <div className="row" style={{ gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="pri"
            disabled={busy || !form.name.trim() || !form.category.trim() || regions.length === 0}
            onClick={() => void submit()}
          >
            {busy ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Выбор категории бренда.
 *
 * Кнопками, а не полем ввода: по категории бренд раскладывается по разделам
 * ленты, и раньше сотрудник не видел исхода, пока не сохранил компанию.
 * «супермаркет», «Супермаркеты» и «сеть магазинов у дома» давали три разных
 * результата, причём последний — «Другое», потому что ни одной приметы в нём
 * нет.
 *
 * Своя категория осталась возможной: бизнесов больше, чем строк в списке. Но
 * это шаг в сторону, а не способ по умолчанию, и раздел ленты у такой
 * категории показывается сразу — чтобы промах был виден до сохранения.
 */
function CategoryPicker({ value, onPick }: { value: string; onPick: (v: string) => void }) {
  const catalog = useResource(() => api.catalog(), []);
  const list = catalog.data?.categories ?? [];
  const known = list.find((c) => c.label === value);
  const [custom, setCustom] = useState(false);
  const typing = custom || (value !== '' && !known);

  return (
    <>
      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
        {list.map((c) => (
          <button
            key={c.label}
            type="button"
            className={`chip ${value === c.label ? 'on' : ''}`}
            title={`Раздел ленты: ${c.sphere_label}`}
            onClick={() => {
              setCustom(false);
              onPick(c.label);
            }}
          >
            {c.label}
          </button>
        ))}
        <button
          type="button"
          className={`chip ${typing ? 'on' : ''}`}
          onClick={() => {
            setCustom(true);
            if (known) onPick('');
          }}
        >
          Своя категория
        </button>
      </div>

      {typing && (
        <input
          className="input"
          style={{ marginTop: 8 }}
          placeholder="Например: зоотовары"
          value={value}
          onChange={(e) => onPick(e.target.value)}
          autoFocus
        />
      )}

      <p className="sub" style={{ fontSize: 11.5, marginTop: 6 }}>
        {known
          ? `Бренд попадёт в раздел ленты «${known.sphere_label}».`
          : value.trim()
            ? 'Раздел ленты определится по словам в категории. Если промахнётся — его можно поправить в таблице.'
            : 'По категории бренд раскладывается по разделам ленты.'}
      </p>
    </>
  );
}

/**
 * Заведение бренда платформой.
 *
 * Публичной регистрации у кабинета нет: пока проект пилотный, за каждым
 * брендом стоит договорённость с живым человеком. Раньше это означало, что
 * компанию заводили руками в базе.
 */
function CreateBrandDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    category: '',
    organizer: '',
    legal_id: '',
    contact: '',
    about: '',
    demo: false,
  });
  const [regions, setRegions] = useState<string[]>(['TJ']);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminCreateBrand({ ...form, regions });
      toast('Бренд заведён');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="h2">Новый бренд</div>
        <p className="sub" style={{ margin: '6px 0 12px' }}>
          Компания заводится непроверенной и на бесплатном тарифе: отметка «проверен» — отдельное
          решение, а не побочный эффект заведения. Акции можно вести за бренд сразу; кабинет он
          заведёт себе сам, когда захочет, — кнопка «Пригласить» ждёт в его строке.
        </p>

        <label className="lab" htmlFor="bname">Название</label>
        <input
          id="bname"
          className="input"
          placeholder="Oila Market"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />

        <label className="lab" htmlFor="bslug" style={{ marginTop: 10 }}>
          Адрес в ссылке
        </label>
        <input
          id="bslug"
          className="input mono"
          placeholder="выведется из названия"
          value={form.slug}
          onChange={(e) => set({ slug: e.target.value })}
        />
        <p className="sub" style={{ fontSize: 11.5, marginTop: 4 }}>
          Страница акции будет жить по адресу /{form.slug || 'бренд'}/акция. После запуска первой
          акции менять его уже нельзя — ссылка уйдёт на упаковку.
        </p>

        <label className="lab" style={{ marginTop: 10 }}>Категория</label>
        <CategoryPicker value={form.category} onPick={(category) => set({ category })} />

        <label className="lab" style={{ marginTop: 10 }}>Страны</label>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          {REGIONS.map((r) => (
            <button
              key={r.code}
              type="button"
              className={`chip ${regions.includes(r.code) ? 'on' : ''}`}
              onClick={() =>
                setRegions((prev) =>
                  prev.includes(r.code) ? prev.filter((x) => x !== r.code) : [...prev, r.code],
                )
              }
            >
              {r.name}
            </button>
          ))}
        </div>

        <label className="lab" htmlFor="borg" style={{ marginTop: 10 }}>
          Организатор
        </label>
        <input
          id="borg"
          className="input"
          placeholder="ООО «Оила»"
          value={form.organizer}
          onChange={(e) => set({ organizer: e.target.value })}
        />
        <p className="sub" style={{ fontSize: 11.5, marginTop: 4 }}>
          Юридическое имя: оно попадёт в правила каждой акции как организатор.
        </p>

        <div className="grid c2" style={{ marginTop: 10 }}>
          <div>
            <label className="lab" htmlFor="bid">ИНН / ЕГРПО</label>
            <input
              id="bid"
              className="input mono"
              value={form.legal_id}
              onChange={(e) => set({ legal_id: e.target.value })}
            />
          </div>
          <div>
            <label className="lab" htmlFor="bcontact">Контакт для участников</label>
            <input
              id="bcontact"
              className="input"
              placeholder="promo@oila.tj"
              value={form.contact}
              onChange={(e) => set({ contact: e.target.value })}
            />
          </div>
        </div>

        <label className="row" style={{ gap: 8, marginTop: 12 }}>
          <input
            type="checkbox"
            checked={form.demo}
            onChange={(e) => set({ demo: e.target.checked })}
          />
          <span className="sub">
            Компания-витрина — её акции не настоящие и нужны, чтобы лента не пустовала
          </span>
        </label>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button
            variant="pri"
            disabled={busy || !form.name.trim() || !form.category.trim() || regions.length === 0}
            onClick={submit}
          >
            {busy ? 'Заводим…' : 'Завести'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Приглашение бренда в кабинет.
 *
 * Ссылка показывается один раз: в базе лежит только её отпечаток. Это не
 * неудобство, а то же правило, по которому нельзя посмотреть чужой пароль —
 * ссылка на кабинет открывает акции и коды бренда.
 */
function InviteDialog({ brand, onClose }: { brand: Brand; onClose: () => void }) {
  const toast = useToast();
  const invites = useResource(() => api.adminBrandInvites(brand.id), [brand.id]);
  const [email, setEmail] = useState(brand.contact ?? '');
  const [role, setRole] = useState<'owner' | 'manager' | 'operator'>('owner');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');

  const issue = async () => {
    setBusy(true);
    try {
      const { url } = await api.adminInviteBrand(brand.id, email.trim(), role);
      setLink(url);
      invites.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invite: BrandInvite) => {
    try {
      await api.adminRevokeInvite(brand.id, invite.id);
      toast('Ссылка отозвана');
      invites.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="h2">Пригласить «{brand.name}» в кабинет</div>
        <p className="sub" style={{ margin: '6px 0 12px' }}>
          Партнёр откроет ссылку и сам придумает пароль — мы его не узнаем. Ссылка живёт две
          недели, срабатывает один раз и привязана к адресу почты.
        </p>

        {link ? (
          <>
            <label className="lab">Ссылка для партнёра</label>
            <input className="input mono" readOnly value={link} onFocus={(e) => e.target.select()} />
            <p className="sub" style={{ fontSize: 11.5, marginTop: 4 }}>
              Скопируй сейчас: второй раз показать её нельзя — в базе лежит только отпечаток.
              Потерялась — выдай новую, а эту отзови.
            </p>
            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <Button
                variant="pri"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(link)
                    .then(() => toast('Ссылка скопирована'))
                    .catch(() => toast('Скопируй вручную — буфер недоступен'));
                }}
              >
                Скопировать
              </Button>
              <span className="spacer" />
              <Button onClick={() => setLink('')}>Выдать ещё одну</Button>
            </div>
          </>
        ) : (
          <>
            <label className="lab" htmlFor="iemail">Почта партнёра</label>
            <input
              id="iemail"
              className="input"
              type="email"
              placeholder="owner@oila.tj"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="lab" htmlFor="irole" style={{ marginTop: 10 }}>
              Роль
            </label>
            <select
              id="irole"
              className="select"
              value={role}
              onChange={(e) => setRole(e.target.value as typeof role)}
            >
              <option value="owner">владелец — всё, включая профиль бренда</option>
              <option value="manager">менеджер — акции и розыгрыши</option>
              <option value="operator">оператор — только выдача призов</option>
            </select>
            <div className="row" style={{ marginTop: 14, gap: 8 }}>
              <Button onClick={onClose}>Закрыть</Button>
              <span className="spacer" />
              <Button variant="pri" disabled={busy || !email.includes('@')} onClick={issue}>
                {busy ? 'Выдаём…' : 'Выдать ссылку'}
              </Button>
            </div>
          </>
        )}

        {(invites.data?.invites.length ?? 0) > 0 && (
          <div style={{ marginTop: 18 }}>
            <div className="lab" style={{ marginBottom: 6 }}>Выданные ссылки</div>
            <table className="tbl">
              <tbody>
                {invites.data?.invites.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <div>{v.email}</div>
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {v.role} · до {fmtShortDay(v.expires_at)} · {v.created_by}
                      </div>
                    </td>
                    <td>
                      <Badge tone={v.state === 'принято' ? 'good' : v.live ? 'warn' : undefined}>
                        {v.state}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {v.live && (
                        <Button size="sm" variant="danger" onClick={() => void revoke(v)}>
                          Отозвать
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
